import React, { useState } from 'react';
import { ShoppingBag, ShieldCheck, Truck, Check, HelpCircle, AlertCircle } from 'lucide-react';
import { useShop } from '../context/ShopContext';

export const ProductDetails: React.FC = () => {
  const { currentProduct, locale, currency, t, addToCart, shippingConfig, storeStatus } = useShop();
  const [quantity, setQuantity] = useState<number>(1);
  const [selectedVariantId, setSelectedVariantId] = useState<string | undefined>(
    currentProduct?.variants?.[0]?.id
  );

  if (!currentProduct) {
    return (
      <div className="p-8 text-center text-gray-500">
        Chargement des informations du produit...
      </div>
    );
  }

  const selectedVariant = currentProduct.variants?.find(v => v.id === selectedVariantId) || currentProduct.variants?.[0];

  const title = locale === 'de' ? currentProduct.name_de : currentProduct.name_fr;
  const description = locale === 'de' ? currentProduct.description_de : currentProduct.description_fr;

  const basePrice = currency === 'CHF' ? currentProduct.price_chf : currentProduct.price_eur;
  const effectivePrice = currency === 'CHF'
    ? (currentProduct.effective_price_chf ?? basePrice)
    : (currentProduct.effective_price_eur ?? basePrice);

  const hasPromotion = Boolean(currentProduct.has_promotion && effectivePrice < basePrice);
  const discountAmount = Math.max(0, Math.round((basePrice - effectivePrice) * 100) / 100);
  const discountPercent = basePrice > 0 ? Math.round((discountAmount / basePrice) * 100) : 0;

  const isSoldOut = currentProduct.track_stock === 1 && currentProduct.stock_quantity <= 0;
  const salesActive = storeStatus?.sales_enabled !== false && storeStatus?.sales_enabled !== 0;

  const handleAdd = () => {
    if (isSoldOut || !salesActive) return;
    addToCart(currentProduct, selectedVariant, quantity);
  };

  const deliveryText = locale === 'de' ? shippingConfig?.delivery_delay_de : shippingConfig?.delivery_delay_fr;
  const showDelivery = Boolean(shippingConfig?.show_delivery_delays && deliveryText);

  return (
    <div className="flex flex-col space-y-6">
      {/* Category / Badge */}
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-pink-100 text-pink-700">
          {t.hero.badge}
        </span>
        {currentProduct.track_stock === 1 && !isSoldOut && (
          <span className="inline-flex items-center gap-1 text-xs text-emerald-600 font-medium">
            <Check className="w-3.5 h-3.5" />
            {t.hero.inStock}
          </span>
        )}
      </div>

      {/* Product Title */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-display font-bold text-gray-900 leading-tight">
          {title}
        </h1>
        <p className="mt-2 text-sm text-gray-600 leading-relaxed">
          {t.hero.subtitle}
        </p>
      </div>

      {/* Pricing display */}
      <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-xs flex items-baseline gap-3">
        <span className="text-3xl font-display font-extrabold text-gray-900">
          {effectivePrice.toFixed(2)} {currency}
        </span>

        {/* Promotion strike-through ONLY if promotion actually active */}
        {hasPromotion && (
          <>
            <span className="text-base font-medium text-gray-400 line-through">
              {basePrice.toFixed(2)} {currency}
            </span>
            <span className="text-xs font-bold bg-pink-500 text-white px-2 py-0.5 rounded-md">
              -{discountPercent}% ({t.hero.save} {discountAmount.toFixed(2)} {currency})
            </span>
          </>
        )}
      </div>

      {/* Variant Selector */}
      {currentProduct.variants && currentProduct.variants.length > 0 && (
        <div className="space-y-2">
          <label className="block text-xs font-bold uppercase tracking-wider text-gray-700">
            {locale === 'de' ? 'Ausführung' : 'Finition & Format'}
          </label>
          <div className="grid grid-cols-1 gap-2">
            {currentProduct.variants.map(variant => (
              <button
                key={variant.id}
                type="button"
                onClick={() => setSelectedVariantId(variant.id)}
                className={`flex items-center justify-between p-3.5 rounded-xl border text-sm text-left transition-all ${
                  selectedVariant?.id === variant.id
                    ? 'border-pink-500 bg-pink-50/40 text-gray-900 font-semibold ring-1 ring-pink-500'
                    : 'border-gray-200 hover:border-gray-300 text-gray-700 bg-white'
                }`}
              >
                <span className="flex items-center gap-2">
                  <span className="w-3.5 h-3.5 rounded-full bg-pink-400 border border-white shadow-xs" />
                  <span>{locale === 'de' ? variant.name_de : variant.name_fr}</span>
                </span>
                <span className="text-xs text-gray-500 font-mono">{variant.sku}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Quantity & CTA */}
      <div className="space-y-3 pt-2">
        <div className="flex items-center gap-4">
          <div className="flex items-center border border-gray-200 bg-white rounded-xl p-1">
            <button
              onClick={() => setQuantity(Math.max(1, quantity - 1))}
              disabled={quantity <= 1 || isSoldOut}
              className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-30"
              aria-label="Diminuer la quantité"
            >
              -
            </button>
            <span className="w-10 text-center text-sm font-semibold font-mono text-gray-900">
              {quantity}
            </span>
            <button
              onClick={() => setQuantity(Math.min(99, quantity + 1))}
              disabled={quantity >= 99 || isSoldOut}
              className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-30"
              aria-label="Augmenter la quantité"
            >
              +
            </button>
          </div>

          <button
            type="button"
            onClick={handleAdd}
            disabled={isSoldOut || !salesActive}
            className={`flex-1 py-3.5 px-6 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all shadow-md ${
              isSoldOut || !salesActive
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed shadow-none'
                : 'bg-neutral-900 hover:bg-pink-600 text-white active:scale-[0.98]'
            }`}
          >
            <ShoppingBag className="w-4 h-4" />
            <span>
              {!salesActive
                ? t.cart.checkoutDisabled
                : isSoldOut
                ? t.hero.ctaSoldOut
                : `${t.hero.ctaAddToCart} • ${(effectivePrice * quantity).toFixed(2)} ${currency}`}
            </span>
          </button>
        </div>

        {/* Security / trust micro-copy */}
        <div className="flex items-center justify-center gap-4 text-xs text-gray-500 pt-1">
          <span className="flex items-center gap-1">
            <ShieldCheck className="w-4 h-4 text-emerald-600" />
            Paiement SSL vérifié
          </span>
          <span className="flex items-center gap-1">
            <Truck className="w-4 h-4 text-gray-600" />
            Colis neutre & discret
          </span>
        </div>
      </div>

      {/* Honest Technical Specifications */}
      <div className="border-t border-gray-100 pt-6 space-y-3">
        <h3 className="text-xs font-bold uppercase tracking-wider text-gray-800">
          {t.hero.specsTitle}
        </h3>
        <ul className="text-xs text-gray-600 space-y-2 bg-white p-4 rounded-xl border border-gray-100">
          <li className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-pink-500" />
            <span>{t.hero.specMaterial}</span>
          </li>
          <li className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-pink-500" />
            <span>{t.hero.specCapacity}</span>
          </li>
          <li className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-pink-500" />
            <span>{t.hero.specInterior}</span>
          </li>
          <li className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-pink-500" />
            <span>{t.hero.specCare}</span>
          </li>
          <li className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-pink-500" />
            <span>{t.hero.specPrint}</span>
          </li>
        </ul>
      </div>

      {/* Honest Delivery Information (ONLY shown if configured) */}
      {showDelivery && (
        <div className="bg-amber-50/60 border border-amber-200/60 p-3.5 rounded-xl text-xs text-amber-900">
          <div className="font-semibold flex items-center gap-1.5 mb-1">
            <Truck className="w-3.5 h-3.5 text-amber-700" />
            <span>{t.hero.shippingTitle}</span>
          </div>
          <p className="leading-relaxed text-amber-800">{deliveryText}</p>
        </div>
      )}
    </div>
  );
};
